import { Skeleton } from "@designs-by-maryam/ui/components/skeleton";
import { cn } from "@designs-by-maryam/ui/lib/utils";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { orpc } from "@/utils/orpc";

/** Default animation duration for stat counters (in milliseconds) */
const COUNTER_ANIMATION_MS = 2000;

type StatItemProps = {
	label: string;
	value: number;
	suffix?: string;
	/**
	 * Animation duration in milliseconds
	 */
	duration?: number;
};

/**
 * StatItem Component
 *
 * Displays a single stat with animated counter
 */
function StatItem({
	label,
	value,
	suffix = "",
	duration = COUNTER_ANIMATION_MS,
}: StatItemProps) {
	const [count, setCount] = useState(0);
	const [isVisible, setIsVisible] = useState(false);
	const [hasAnimated, setHasAnimated] = useState(false);
	const elementRef = useRef<HTMLDivElement>(null);

	// Check for reduced motion preference
	const prefersReducedMotion =
		typeof window !== "undefined" &&
		window.matchMedia("(prefers-reduced-motion: reduce)").matches;

	// Intersection Observer to trigger animation when in viewport
	useEffect(() => {
		if (hasAnimated || !elementRef.current) {
			return;
		}

		const observer = new IntersectionObserver(
			(entries) => {
				if (entries[0]?.isIntersecting) {
					setIsVisible(true);
					setHasAnimated(true);
				}
			},
			{ threshold: 0.5 },
		);

		observer.observe(elementRef.current);

		return () => {
			observer.disconnect();
		};
	}, [hasAnimated]);

	// Animate counter when visible
	useEffect(() => {
		if (!isVisible) {
			return;
		}

		// If reduced motion, set immediately
		if (prefersReducedMotion) {
			setCount(value);
			return;
		}

		const startTime = performance.now();
		const startValue = 0;
		const endValue = value;

		function easeOutQuad(t: number): number {
			return t * (2 - t);
		}

		function animate(currentTime: number) {
			const elapsed = currentTime - startTime;
			const progress = Math.min(elapsed / duration, 1);
			const easedProgress = easeOutQuad(progress);

			const currentCount = Math.floor(
				startValue + (endValue - startValue) * easedProgress,
			);
			setCount(currentCount);

			if (progress < 1) {
				requestAnimationFrame(animate);
			} else {
				setCount(endValue);
			}
		}

		requestAnimationFrame(animate);
	}, [isVisible, value, duration, prefersReducedMotion]);

	return (
		<div
			className="group relative flex flex-col items-center gap-3 overflow-hidden rounded-2xl bg-gradient-to-br from-muted/80 to-muted/40 p-8 shadow-lg ring-1 ring-border/50 backdrop-blur-sm transition-shadow duration-300 hover:shadow-xl hover:ring-primary/50"
			data-testid="stat-item"
			ref={elementRef}
		>
			{/* Background pattern overlay */}
			<div
				aria-hidden="true"
				className="absolute inset-0 opacity-[0.03] transition-opacity duration-500 group-hover:opacity-[0.06]"
				style={{
					backgroundImage: `linear-gradient(to right, currentColor 1px, transparent 1px),
                           linear-gradient(to bottom, currentColor 1px, transparent 1px)`,
					backgroundSize: "24px 24px",
				}}
			/>

			{/* Subtle gradient accent on hover */}
			<div
				aria-hidden="true"
				className="-right-10 -top-10 absolute h-24 w-24 rounded-full bg-gradient-to-br from-primary/10 to-blue-500/10 opacity-0 blur-lg transition-opacity duration-500 group-hover:opacity-100"
			/>

			<div
				aria-live="polite"
				className="relative bg-gradient-to-br from-primary via-primary/80 to-primary/60 bg-clip-text font-bold text-5xl text-transparent md:text-6xl"
				data-testid="stat-value"
			>
				{count}
				{suffix}
			</div>
			<div
				className="relative text-center font-semibold text-muted-foreground text-sm tracking-wide transition-colors duration-300 group-hover:text-foreground/90 md:text-base"
				data-testid="stat-label"
			>
				{label}
			</div>
		</div>
	);
}

type StatsSectionProps = {
	/**
	 * Additional className for customization
	 */
	className?: string;
};

/**
 * StatsSection Component
 *
 * Displays key statistics with animated counters.
 *
 * Features:
 * - Fetches stats from backend API
 * - Animated counters using requestAnimationFrame
 * - Respects prefers-reduced-motion
 * - Intersection Observer for viewport-triggered animation
 * - Loading and error states
 */
export function StatsSection({ className }: StatsSectionProps) {
	const stats = useQuery(orpc.getPublicStats.queryOptions());

	if (stats.isLoading) {
		return (
			<section
				aria-label="Statistics"
				className={cn("container mx-auto px-4 py-12 md:py-16", className)}
				data-testid="stats-section"
			>
				<div className="grid gap-6 md:grid-cols-3">
					<Skeleton className="h-32 rounded-lg" />
					<Skeleton className="h-32 rounded-lg" />
					<Skeleton className="h-32 rounded-lg" />
				</div>
			</section>
		);
	}

	if (stats.error || !stats.data) {
		return (
			<section
				aria-label="Statistics"
				className={cn("container mx-auto px-4 py-12 md:py-16", className)}
				data-testid="stats-section"
			>
				<div className="text-center text-muted-foreground">
					<p>Unable to load statistics. Please try again later.</p>
				</div>
			</section>
		);
	}

	// Safe access with defaults - stats.data is validated by the error check above
	const projectsCompleted = stats.data.data.projectsCompleted ?? 0;
	const yearsExperience = stats.data.data.yearsExperience ?? 0;
	const clientSatisfaction = stats.data.data.clientSatisfaction ?? 0;

	return (
		<section
			aria-label="Statistics"
			className={cn(
				"relative overflow-hidden bg-gradient-to-b from-background via-muted/20 to-background px-4 py-16 md:py-24",
				className,
			)}
			data-testid="stats-section"
		>
			{/* Enhanced grid background pattern */}
			<div
				aria-hidden="true"
				className="absolute inset-0 opacity-[0.04]"
				style={{
					backgroundImage: `linear-gradient(to right, currentColor 1px, transparent 1px),
                           linear-gradient(to bottom, currentColor 1px, transparent 1px)`,
					backgroundSize: "32px 32px",
				}}
			/>

			{/* Subtle decorative elements */}
			<div
				aria-hidden="true"
				className="absolute top-10 left-1/4 h-48 w-48 rounded-full bg-gradient-to-br from-primary/5 to-purple-500/5 blur-xl"
			/>
			<div
				aria-hidden="true"
				className="absolute right-1/4 bottom-10 h-48 w-48 rounded-full bg-gradient-to-br from-blue-500/5 to-pink-500/5 blur-xl"
			/>

			<div className="container relative mx-auto">
				<div className="mx-auto grid max-w-6xl gap-8 md:grid-cols-3">
					<StatItem
						label="Completed Projects"
						suffix="+"
						value={projectsCompleted}
					/>
					<StatItem
						label="Years of Experience"
						suffix="+"
						value={yearsExperience}
					/>
					<StatItem
						label="Client Satisfaction"
						suffix="%"
						value={clientSatisfaction}
					/>
				</div>
			</div>
		</section>
	);
}
